import React from "react";
import { Box, Typography, CircularProgress, Alert, useTheme } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useInstitutionalOwnership } from "../hooks/useInstitutionalOwnership";
import InstitutionalOwnershipPieChart from "./InstitutionalOwnershipPieChart";
import InstitutionalOwnershipTable from "./InstitutionalOwnershipTable";
import { roundedPanelSx } from "../../overviewPanel";

const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
    border: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
    textPrimary: isDark ? "#ffffff" : "#1d1d1f",
    textSecondary: isDark ? "#9ca3af" : "#64748b",
    textMuted: isDark ? "#6b7280" : "#94a3b8",
  };
};

interface InstitutionalOwnershipTabProps {
  ticker: string;
}

export const InstitutionalOwnershipTab: React.FC<InstitutionalOwnershipTabProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);

  const {
    data: ownershipData,
    error,
    isLoading,
  } = useInstitutionalOwnership(ticker);

  if (isLoading) {
    return (
      <Box
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // Check if it's a 404 (no data found) vs actual error
    if (error.message?.includes("404") || error.message?.includes("No institutional")) {
      return (
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: flatUIColors.textSecondary,
              mb: 2,
            }}
          >
            No Institutional Ownership Data Found
          </Typography>
          <Typography
            sx={{
              color: flatUIColors.textMuted,
              maxWidth: "500px",
            }}
          >
            This company may not have institutional 13F filings available, which is common for smaller companies, recent IPOs, or international stocks.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <Alert severity="error">
          Error loading institutional ownership data: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!ownershipData || ownershipData.holders.length === 0) {
    return (
      <Box
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
          py: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: flatUIColors.textSecondary,
            mb: 2,
          }}
        >
          No Institutional Ownership Data Found
        </Typography>
        <Typography
          sx={{
            color: flatUIColors.textMuted,
            maxWidth: "500px",
          }}
        >
          This company may not have institutional 13F filings available, which is common for smaller companies, recent IPOs, or international stocks.
        </Typography>
      </Box>
    );
  }

  const { holders, metadata } = ownershipData;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Pie Chart - Top 10 holders */}
      <InstitutionalOwnershipPieChart
        holders={holders}
        quarterLabel={metadata.quarterLabel}
      />

      {/* Table - Top 15 holders */}
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <InstitutionalOwnershipTable holders={holders} />
      </Box>
    </Box>
  );
};
